import React, { useState } from "react";
import { useSelector } from "react-redux";

import type { CareTeamMember, Goal, Objective, PatientDetails } from "@/core/types";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { selectProfile } from "@/redux/selectors/authSelectors";

import { useGoalAndObjectiveText } from "../../api/queries/useGoalAndObjectiveText";
import { useGoalsAndObjectives } from "../../api/queries/useGoalsAndObjectives";

import { CareGoalModal } from "./components/CareGoalModal";
import { CareGoalRoleEntry } from "./components/CareGoalRoleEntry";
import {
  flattenGoals,
  flattenObjectives,
  getCurrentCareTeamMembersByType,
  getGoalsMap,
  getObjectivesMap,
  getUniqueCurrentAndPastCareTeamRoles,
} from "./helpers";
import { MODAL_MODES, PROVIDER_TYPE_TO_LABEL } from "./constants";

import "./GoalsAndObjectives.styles.scss";

type ModalMode = (typeof MODAL_MODES)[keyof typeof MODAL_MODES];

type Props = {
  careTeamMembers: CareTeamMember[];
  isProviderOnCareTeam: boolean;
  patientDetails: PatientDetails;
  patientId: string;
  refreshCareTeam: () => Promise<unknown>;
};

export function GoalsAndObjectives({
  careTeamMembers,
  isProviderOnCareTeam,
  patientDetails,
  patientId,
  refreshCareTeam,
}: Props) {
  const profile = useSelector(selectProfile);

  const { data: goalAndObjectives = {}, refetch: refetchGoalsAndObjectives } =
    useGoalsAndObjectives({ patientId });
  const { data: goalsAndObjectiveOptions } = useGoalAndObjectiveText({ patientId });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>(MODAL_MODES.ADD_GOAL);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [selectedObjective, setSelectedObjective] = useState<Objective | null>(null);
  const [objectiveCloseMode, setObjectiveCloseMode] = useState<string | null>(null);

  const providerType = (profile as { provider_type?: string } | undefined)?.provider_type;
  const [activeEditingProviderType, setActiveEditingProviderType] = useState<string | undefined>(
    providerType,
  );

  const rolesToShow = getUniqueCurrentAndPastCareTeamRoles(careTeamMembers);
  const careTeamMembersByRole = getCurrentCareTeamMembersByType(careTeamMembers) as Record<
    string,
    CareTeamMember[]
  >;

  const providerGoalsAndObjectiveOptions =
    (activeEditingProviderType != null && goalsAndObjectiveOptions?.[activeEditingProviderType]) ||
    [];
  const providerGoalOptions = flattenGoals(providerGoalsAndObjectiveOptions);
  const providerObjectiveOptions = flattenObjectives(providerGoalsAndObjectiveOptions);
  const providerGoalOptionsMap = getGoalsMap(providerGoalsAndObjectiveOptions);
  const providerObjectiveOptionsMap = getObjectivesMap(providerGoalsAndObjectiveOptions);
  const providerLabel =
    (PROVIDER_TYPE_TO_LABEL as Record<string, string>)[activeEditingProviderType ?? ""] ?? "";

  const onCloseModal = () => {
    setShowModal(false);
    setModalMode(MODAL_MODES.EMPTY);
    setSelectedGoal(null);
    setSelectedObjective(null);
    setObjectiveCloseMode(null);
  };

  const onModalSubmission = async () => {
    await refetchGoalsAndObjectives();
    onCloseModal();
  };

  const getProviderTypeFromGoal = (goal: Goal): string => goal.provider_type;

  const openAddGoalModal = (role: string) => {
    setActiveEditingProviderType(role);
    setModalMode(MODAL_MODES.ADD_GOAL);
    setShowModal(true);
  };

  const openAddObjectiveModal = (goal: Goal) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.ADD_OBJECTIVE);
    setSelectedGoal(goal);
    setShowModal(true);
  };

  const openEditObjectiveModal = (goal: Goal, objective: Objective) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.EDIT_OBJECTIVE);
    setSelectedGoal(goal);
    setSelectedObjective(objective);
    setShowModal(true);
  };

  const openCompleteObjectiveModal = (goal: Goal, objective: Objective) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.COMPLETE_OBJECTIVE);
    setSelectedGoal(goal);
    setSelectedObjective(objective);
    setShowModal(true);
  };

  const openRemoveObjectiveModal = (goal: Goal, objective: Objective) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.REMOVE_OBJECTIVE);
    setSelectedGoal(goal);
    setSelectedObjective(objective);
    setShowModal(true);
  };

  const openCloseAndAddObjectiveModal = (
    goal: Goal,
    objective: Objective,
    newObjectiveStatus: string,
  ) => {
    setActiveEditingProviderType(getProviderTypeFromGoal(goal));
    setModalMode(MODAL_MODES.CLOSE_AND_ADD_OBJECTIVE);
    setSelectedObjective(objective);
    setSelectedGoal(goal);
    setObjectiveCloseMode(newObjectiveStatus);
    setShowModal(true);
  };

  if (rolesToShow.length === 0) {
    return null;
  }

  return (
    <DashboardCard2 title="Care Goals & Objectives">
      <div className="CareGoalsCard--role_container">
        <CareGoalModal
          goal={selectedGoal}
          modalMode={modalMode}
          objective={selectedObjective}
          objectiveCloseMode={objectiveCloseMode}
          onClose={onCloseModal}
          onCloseAndAddObjective={openCloseAndAddObjectiveModal}
          onCompleteObjectiveClick={openCompleteObjectiveModal}
          onModalSubmission={onModalSubmission}
          onRemoveObjectiveClick={openRemoveObjectiveModal}
          patientDetails={patientDetails}
          providerGoalOptions={providerGoalOptions}
          providerGoalOptionsMap={providerGoalOptionsMap}
          providerLabel={providerLabel}
          providerObjectiveOptions={providerObjectiveOptions}
          providerObjectiveOptionsMap={providerObjectiveOptionsMap}
          providerTypeCurrentGoalsAndObjectives={goalAndObjectives[activeEditingProviderType ?? ""]}
          show={showModal}
        />

        {Object.keys(PROVIDER_TYPE_TO_LABEL).map((key) => {
          if (
            !rolesToShow.includes(
              PROVIDER_TYPE_TO_LABEL[key as keyof typeof PROVIDER_TYPE_TO_LABEL],
            )
          ) {
            return null;
          }

          return (
            <CareGoalRoleEntry
              careTeamMembersForRole={
                careTeamMembersByRole?.[
                  PROVIDER_TYPE_TO_LABEL[key as keyof typeof PROVIDER_TYPE_TO_LABEL]
                ]
              }
              isProviderOnCareTeam={isProviderOnCareTeam}
              key={key}
              label={PROVIDER_TYPE_TO_LABEL[key as keyof typeof PROVIDER_TYPE_TO_LABEL]}
              onAddGoalClick={() => openAddGoalModal(key)}
              onAddObjectiveClick={openAddObjectiveModal}
              onCompleteObjectiveClick={openCompleteObjectiveModal}
              onEditObjectiveClick={openEditObjectiveModal}
              onRemoveObjectiveClick={openRemoveObjectiveModal}
              patientId={patientId}
              profile={profile}
              providerType={providerType}
              providerTypeCurrentGoalsAndObjectives={goalAndObjectives[key]}
              refreshCareTeam={refreshCareTeam}
              role={key}
            />
          );
        })}
      </div>
    </DashboardCard2>
  );
}
